import { useEffect, useState, useCallback } from "react";
import { RESTAURANT_INFO } from "../data/menu";
import {
  fetchPhone,
  savePhone as savePhoneApi,
  loginApi,
  logoutApi,
  getToken,
  clearToken,
  ApiError,
} from "../api";

// ---------- Phone number (server-side, shared) ----------
const phoneListeners = new Set();
let currentPhone = RESTAURANT_INFO.phone;
let phoneLoaded = false;

function notifyPhone(phone) {
  currentPhone = phone;
  phoneListeners.forEach((l) => l(phone));
}

async function loadPhone() {
  try {
    const remote = await fetchPhone();
    if (remote && remote.length > 0) return remote;
  } catch {
    /* offline / not seeded */
  }
  return RESTAURANT_INFO.phone;
}

export async function updatePhoneNumber(next) {
  const cleaned = (next || "").trim();
  if (!cleaned) return false;
  try {
    const saved = await savePhoneApi(cleaned);
    notifyPhone(saved);
    return true;
  } catch {
    return false;
  }
}

export function formatPhone(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length >= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  return raw;
}

export function usePhoneNumber() {
  const [phone, setPhone] = useState(currentPhone);

  useEffect(() => {
    let mounted = true;
    if (!phoneLoaded) {
      phoneLoaded = true;
      loadPhone().then((p) => {
        if (!mounted) return;
        notifyPhone(p);
      });
    }
    const l = (p) => setPhone(p);
    phoneListeners.add(l);
    return () => {
      mounted = false;
      phoneListeners.delete(l);
    };
  }, []);

  return { phone, phoneDisplay: formatPhone(phone) };
}

// ---------- Admin auth (JWT via backend) ----------
const authListeners = new Set();
let adminAuthenticated = null;

function notifyAuth(v) {
  adminAuthenticated = v;
  authListeners.forEach((f) => f(v));
}

function bootstrapAuth() {
  const t = getToken();
  notifyAuth(!!t);
}

export function useAdminAuth() {
  const [state, setState] = useState({
    isAuth: !!adminAuthenticated,
    loaded: adminAuthenticated !== null,
  });

  useEffect(() => {
    if (adminAuthenticated === null) {
      bootstrapAuth();
    }
    const l = (v) => setState({ isAuth: v, loaded: true });
    authListeners.add(l);
    return () => {
      authListeners.delete(l);
    };
  }, []);

  const login = useCallback(async (user, pass) => {
    try {
      await loginApi(user.trim(), pass);
      notifyAuth(true);
      return null;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        return "Credenziali non valide. Riprova.";
      }
      return "Impossibile connettersi al server. Riprova più tardi.";
    }
  }, []);

  const logout = useCallback(async () => {
    await logoutApi();
    notifyAuth(false);
  }, []);

  return { isAuthenticated: state.isAuth, loaded: state.loaded, login, logout };
}

export async function forceLogout() {
  clearToken();
  notifyAuth(false);
}
